import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useQuery, useMutation, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import { useApi } from '../hooks/useApi';
import Button from '../components/ui/Button';
import { Input, Select, Label } from '../components/ui/Input';
import DataGrid from '../components/grid/DataGrid';
import PlanogramViewer, { brandColor } from '../components/planogram/PlanogramViewer';
import { useBlob } from '../hooks/useBlob';

// Scan overlay: planogram customer → scan account key in the overlay blob.
const ACCOUNT_BY_CUS = { CUS0044: 'ww', CUS0027: 'coles' };

// Space Planning (migration 051). Central, effective-dated record of retail
// planograms (POGs) at customer x sub-category x layout grain, plus an in-app
// viewer for the ProSpace .PSA format. Register + upload (dry-run → commit) +
// viewer + share-of-shelf-over-time trend. Governed writes (requireCommercialWrite
// + audit) live server-side; this page never parses — the API owns the parser.

const fmtDate = (s) => {
  if (!s) return '—';
  const d = new Date(s);
  return isNaN(d.getTime()) ? '—' : d.toLocaleDateString('en-AU', { day: '2-digit', month: 'short', year: 'numeric' });
};
const CHIP = { display: 'inline-flex', alignItems: 'center', padding: '1px 10px', borderRadius: 999, fontSize: 11, fontWeight: 600, lineHeight: 1.7, whiteSpace: 'nowrap', textTransform: 'capitalize' };
// Soft-filled status badges, matching the app's token convention (success / warn /
// neutral). `bg` + `border` + `color` per state so the pill reads as a proper chip.
const STATUS_STYLE = {
  active: { color: 'var(--success)', bg: 'var(--success-soft)', border: 'var(--success-border)' },
  draft: { color: 'var(--warn-text)', bg: 'var(--warn-soft)', border: 'var(--warn-border)' },
  superseded: { color: 'var(--text-muted)', bg: 'var(--border-soft)', border: 'var(--border)' },
};
const LINK = { background: 'none', border: 'none', color: 'var(--accent)', cursor: 'pointer', fontSize: 12, fontWeight: 600, padding: 0 };

// BayGeometry is stored as a JSON string ([{bay,xOrigin,width}]); parse defensively.
function parseBayGeometry(v) {
  if (!v) return null;
  if (Array.isArray(v)) return v;
  try { return JSON.parse(v); } catch { return null; }
}

function fileToBase64(file) {
  return file.arrayBuffer().then((buf) => {
    const bytes = new Uint8Array(buf);
    let bin = '';
    const CH = 0x8000;
    for (let i = 0; i < bytes.length; i += CH) bin += String.fromCharCode(...bytes.subarray(i, i + CH));
    return btoa(bin);
  });
}

export default function SpacePlanning() {
  const apiFetch = useApi();
  const qc = useQueryClient();
  const fileRef = useRef(null);
  const [view, setView] = useState('register'); // register | trends
  const [customer, setCustomer] = useState('All');
  const [subCategory, setSubCategory] = useState('All');
  const [currentOnly, setCurrentOnly] = useState(false);
  const [detailId, setDetailId] = useState(null);
  const [editRow, setEditRow] = useState(null); // the register row being edited (opens the edit panel)
  const [upload, setUpload] = useState(null); // { fileName, base64, preview?, form, committing?, error?, result? }

  const { data: customers } = useQuery({
    queryKey: ['pog-customers'],
    queryFn: () => apiFetch('/master-customers').then((r) => r.json()),
    staleTime: 300_000,
  });
  const customerList = Array.isArray(customers) ? customers : (customers?.rows ?? []);

  const { data: subCats } = useQuery({
    queryKey: ['pog-subcats'],
    queryFn: () => apiFetch('/planograms/sub-categories').then((r) => r.json()),
    staleTime: 300_000,
  });
  const subCategoryList = subCats?.subCategories ?? [];

  const { data, isLoading, error } = useQuery({
    queryKey: ['planograms', customer, subCategory, currentOnly],
    queryFn: () => {
      const sp = new URLSearchParams();
      if (customer !== 'All') sp.set('customer', customer);
      if (subCategory !== 'All') sp.set('subCategory', subCategory);
      if (currentOnly) sp.set('current', 'true');
      return apiFetch(`/planograms?${sp.toString()}`).then((r) => { if (!r.ok) throw new Error(`API ${r.status}`); return r.json(); });
    },
    placeholderData: keepPreviousData,
    staleTime: 30_000,
  });
  const rows = data?.rows ?? [];

  const columns = useMemo(() => [
    {
      headerName: '', field: '__actions', pinned: 'left', width: 104, sortable: false, filter: false, resizable: false,
      cellStyle: { display: 'flex', alignItems: 'center' },
      cellRenderer: (p) => (p.data
        ? (
          <span style={{ display: 'inline-flex', gap: 12 }}>
            <button onClick={() => setDetailId(p.data.planogramId)} style={LINK}>View</button>
            <button onClick={() => setEditRow(p.data)} style={{ ...LINK, color: 'var(--text-muted)' }}>Edit</button>
          </span>
        ) : null),
    },
    { headerName: 'Customer', field: 'customerName', width: 130 },
    { headerName: 'Sub-Category', field: 'subCategory', width: 150 },
    { headerName: 'Layout', field: 'layoutName', flex: 1, minWidth: 200 },
    {
      headerName: 'Status', field: 'status', width: 130,
      cellStyle: { display: 'flex', alignItems: 'center' },
      cellRenderer: (p) => {
        const st = STATUS_STYLE[p.value] ?? STATUS_STYLE.superseded;
        return <span style={{ ...CHIP, color: st.color, background: st.bg, border: `1px solid ${st.border}` }}>{p.value}</span>;
      },
    },
    { headerName: 'Effective', field: 'effectiveFrom', width: 120, valueFormatter: (p) => fmtDate(p.value) },
    { headerName: 'To', field: 'effectiveTo', width: 120, valueFormatter: (p) => (p.value ? fmtDate(p.value) : 'current') },
    { headerName: 'Bays', field: 'bayCount', width: 80, cellDataType: 'number' },
    { headerName: 'SKUs', field: 'positionCount', width: 80, cellDataType: 'number' },
    { headerName: 'Facings', field: 'totalFacings', width: 90, cellDataType: 'number' },
    {
      headerName: 'Majans SoS', field: 'ownBrandSharePct', width: 110,
      valueFormatter: (p) => (p.value == null ? '—' : `${Number(p.value).toFixed(1)}%`),
    },
    { headerName: 'Uploaded', field: 'uploadedAt', width: 130, valueFormatter: (p) => fmtDate(p.value) },
  ], []);

  // ── Upload flow ──
  async function onPickFile(e) {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    const base64 = await fileToBase64(file);
    setUpload({ fileName: file.name, base64, loading: true });
    try {
      const res = await apiFetch('/planograms/import', {
        method: 'POST', body: JSON.stringify({ fileBase64: base64, fileName: file.name, dryRun: true }),
      });
      const preview = await res.json();
      if (!res.ok) throw new Error(preview?.error ?? `Preview failed (${res.status})`);
      const r = preview.resolved ?? {};
      setUpload((s) => ({
        ...s, loading: false, preview,
        form: {
          customerCode: r.customerCode ?? '', subCategory: r.subCategory ?? '',
          layoutName: r.layoutName ?? '', effectiveFrom: r.effectiveFrom ?? '',
          name: r.name ?? '', supersede: true,
        },
      }));
    } catch (err) { setUpload((s) => ({ ...s, loading: false, error: String(err.message ?? err) })); }
  }

  async function commitUpload(activate = true) {
    setUpload((s) => ({ ...s, committing: true, error: null }));
    try {
      const { fileName, base64, form } = upload;
      const res = await apiFetch('/planograms/import', {
        method: 'POST',
        // activate=false lands it as a draft (review first, then Activate from the
        // edit panel); activate=true makes it the current version now (supersedes prior).
        body: JSON.stringify({ fileBase64: base64, fileName, dryRun: false, activate, ...form }),
      });
      const result = await res.json();
      if (!res.ok) throw new Error(result?.error ?? `Import failed (${res.status})`);
      qc.invalidateQueries({ queryKey: ['planograms'] });
      setUpload((s) => ({ ...s, committing: false, result }));
    } catch (err) { setUpload((s) => ({ ...s, committing: false, error: String(err.message ?? err) })); }
  }

  const setForm = (k) => (v) => setUpload((s) => ({ ...s, form: { ...s.form, [k]: v } }));

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
        <div style={{ display: 'flex', gap: 6 }}>
          {[['register', 'Register'], ['trends', 'Trends']].map(([id, label]) => (
            <button key={id} onClick={() => setView(id)}
              style={{ padding: '5px 14px', borderRadius: 6, cursor: 'pointer', fontSize: 13, fontWeight: 600,
                       border: '1px solid ' + (view === id ? 'var(--accent)' : 'var(--border-soft)'),
                       background: view === id ? 'var(--accent-soft)' : 'transparent',
                       color: view === id ? 'var(--accent)' : 'var(--text-body)' }}>{label}</button>
          ))}
        </div>
        <span style={{ flex: 1 }} />
        <Button size="sm" variant="primary" onClick={() => fileRef.current?.click()}>Import POG (.PSA)</Button>
        <input ref={fileRef} type="file" accept=".psa,.PSA" style={{ display: 'none' }} onChange={onPickFile} />
      </div>

      {view === 'register' && (
        <>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'flex-end' }}>
            <div style={{ width: 180 }}>
              <Label>Customer</Label>
              <Select value={customer} onChange={(e) => setCustomer(e.target.value)}>
                <option>All</option>
                {customerList.map((c) => <option key={c.customerCode} value={c.customerCode}>{c.name}</option>)}
              </Select>
            </div>
            <div style={{ width: 200 }}>
              <Label>Sub-Category</Label>
              <Select value={subCategory} onChange={(e) => setSubCategory(e.target.value)}>
                <option>All</option>
                {subCategoryList.map((s) => <option key={s} value={s}>{s}</option>)}
              </Select>
            </div>
            <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, color: 'var(--text-body)', paddingBottom: 6 }}>
              <input type="checkbox" checked={currentOnly} onChange={(e) => setCurrentOnly(e.target.checked)} /> Current only
            </label>
          </div>

          {error && <div style={{ fontSize: 12, color: 'var(--danger)' }}>Failed to load planograms: {String(error.message ?? error)}</div>}

          <DataGrid
            rowData={rows}
            columnDefs={columns}
            getRowId={(p) => String(p.data.planogramId)}
            loading={isLoading}
          />
          <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>
            {rows.length.toLocaleString()} planogram version{rows.length === 1 ? '' : 's'} · View to open the shelf visual · Import a .PSA to add a version
          </div>
        </>
      )}

      {view === 'trends' && (
        <TrendsView apiFetch={apiFetch} customers={customerList} subCategories={subCategoryList} onOpen={setDetailId} />
      )}

      {detailId && <PlanogramDrawer id={detailId} onClose={() => setDetailId(null)} apiFetch={apiFetch} qc={qc} />}
      {editRow && <EditDrawer row={editRow} onClose={() => setEditRow(null)} apiFetch={apiFetch} qc={qc} subCategories={subCategoryList} />}
      {upload && <UploadModal upload={upload} customers={customerList} subCategories={subCategoryList}
        setForm={setForm} onCommit={commitUpload} onClose={() => setUpload(null)} />}
    </div>
  );
}

// ── Trends: own-brand share of shelf over versions ──
function TrendsView({ apiFetch, customers, subCategories, onOpen }) {
  const [cust, setCust] = useState('');
  const [sub, setSub] = useState('');
  const { data } = useQuery({
    queryKey: ['pog-trend', cust, sub],
    queryFn: () => apiFetch(`/planograms/trend?customer=${encodeURIComponent(cust)}&subCategory=${encodeURIComponent(sub)}`).then((r) => r.json()),
    enabled: !!cust && !!sub,
    staleTime: 30_000,
  });
  const versions = data?.versions ?? [];
  const chart = versions.map((v) => ({
    date: fmtDate(v.effectiveFrom),
    share: v.ownBrandSharePct == null ? null : Number(v.ownBrandSharePct),
    facings: v.ownBrandFacings ?? 0,
  }));
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'flex-end' }}>
        <div style={{ width: 180 }}>
          <Label>Customer</Label>
          <Select value={cust} onChange={(e) => setCust(e.target.value)}>
            <option value="">Select…</option>
            {customers.map((c) => <option key={c.customerCode} value={c.customerCode}>{c.name}</option>)}
          </Select>
        </div>
        <div style={{ width: 220 }}>
          <Label>Sub-Category</Label>
          <Select value={sub} onChange={(e) => setSub(e.target.value)}>
            <option value="">Select…</option>
            {subCategories.map((s) => <option key={s} value={s}>{s}</option>)}
          </Select>
        </div>
      </div>
      {!sub && <div style={{ fontSize: 13, color: 'var(--text-muted)' }}>Pick a customer and sub-category to see Majans share of shelf over time.</div>}
      {sub && versions.length === 0 && <div style={{ fontSize: 13, color: 'var(--text-muted)' }}>No planogram versions recorded for this combination yet.</div>}
      {sub && versions.length > 0 && (
        <>
          <div style={{ height: 320, background: 'var(--bg-card)', border: '1px solid var(--border-soft)', borderRadius: 'var(--radius, 8px)', padding: 12 }}>
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={chart} margin={{ top: 8, right: 24, bottom: 8, left: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--border-soft)" />
                <XAxis dataKey="date" tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 11 }} unit="%" />
                <Tooltip formatter={(v) => (v == null ? '—' : `${Number(v).toFixed(1)}%`)} />
                <Legend />
                <Line type="monotone" dataKey="share" name="Majans share of shelf" stroke={brandColor('majans')} strokeWidth={2} connectNulls />
              </LineChart>
            </ResponsiveContainer>
          </div>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ textAlign: 'left', color: 'var(--text-muted)', borderBottom: '1px solid var(--border)' }}>
                <th style={{ padding: '6px 8px' }}>Effective</th><th style={{ padding: '6px 8px' }}>Layout</th>
                <th style={{ padding: '6px 8px' }}>Status</th><th style={{ padding: '6px 8px' }}>Total facings</th>
                <th style={{ padding: '6px 8px' }}>Majans facings</th><th style={{ padding: '6px 8px' }}>Majans SoS</th><th />
              </tr>
            </thead>
            <tbody>
              {versions.map((v) => (
                <tr key={v.planogramId} style={{ borderBottom: '1px solid var(--border-soft)' }}>
                  <td style={{ padding: '6px 8px' }}>{fmtDate(v.effectiveFrom)}</td>
                  <td style={{ padding: '6px 8px' }}>{v.layoutName}</td>
                  <td style={{ padding: '6px 8px' }}>{v.status}</td>
                  <td style={{ padding: '6px 8px' }}>{v.totalFacings ?? '—'}</td>
                  <td style={{ padding: '6px 8px' }}>{v.ownBrandFacings ?? '—'}</td>
                  <td style={{ padding: '6px 8px' }}>{v.ownBrandSharePct == null ? '—' : `${Number(v.ownBrandSharePct).toFixed(1)}%`}</td>
                  <td style={{ padding: '6px 8px' }}>
                    <button onClick={() => onOpen(v.planogramId)} style={{ background: 'none', border: 'none', color: 'var(--accent)', cursor: 'pointer', fontSize: 12, fontWeight: 600 }}>View</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}
    </div>
  );
}

// ── Detail drawer: viewer + share of shelf + download + activate ──
// ── Edit panel: metadata edit + draft actions (activate / delete) ──
// Grain columns (sub-category / layout / effective date) are the temporal identity;
// the API locks them once a version is active/superseded, so we disable them here
// too. Name + notes stay editable on any status.
function EditDrawer({ row, onClose, apiFetch, qc, subCategories }) {
  const id = row.planogramId;
  const isDraft = row.status === 'draft';
  const [form, setForm] = useState({
    name: row.name ?? '',
    notes: '',
    subCategory: row.subCategory ?? '',
    layoutName: row.layoutName ?? '',
    effectiveFrom: String(row.effectiveFrom ?? '').slice(0, 10),
    effectiveTo: row.effectiveTo ? String(row.effectiveTo).slice(0, 10) : '',
    status: row.status ?? 'draft',
  });
  const [confirmDelete, setConfirmDelete] = useState(false);
  const notesLoaded = useRef(false);

  // Pull the full header for Notes (the register list doesn't carry it). Seeded
  // from the row so the form is populated instantly while this resolves.
  const { data: full } = useQuery({
    queryKey: ['planogram', id],
    queryFn: () => apiFetch(`/planograms/${id}`).then((r) => r.json()),
    staleTime: 30_000,
  });
  useEffect(() => {
    if (full?.header && !notesLoaded.current) {
      notesLoaded.current = true;
      setForm((f) => ({ ...f, notes: full.header.notes ?? '' }));
    }
  }, [full]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ['planograms'] });
    qc.invalidateQueries({ queryKey: ['planogram', id] });
    qc.invalidateQueries({ queryKey: ['pog-trend'] });
  };

  const save = useMutation({
    mutationFn: async () => {
      const body = {
        name: form.name.trim() || null,
        notes: form.notes.trim() || null,
        status: form.status,
        // effective-to blank = current (open interval); send null to clear it.
        effectiveTo: form.effectiveTo ? form.effectiveTo : null,
      };
      if (form.effectiveFrom) body.effectiveFrom = form.effectiveFrom;
      if (isDraft) {
        // Sub-category / layout are the grain identity — only mutable while a draft.
        if (form.subCategory) body.subCategory = form.subCategory;
        if (form.layoutName.trim()) body.layoutName = form.layoutName.trim();
      }
      const res = await apiFetch(`/planograms/${id}`, { method: 'PUT', body: JSON.stringify(body) });
      const j = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(j?.error ?? `Save failed (${res.status})`);
      return j;
    },
    onSuccess: () => { invalidate(); onClose(); },
  });

  const activate = useMutation({
    mutationFn: async () => {
      const res = await apiFetch(`/planograms/${id}/activate`, { method: 'PUT' });
      const j = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(j?.error ?? `Activate failed (${res.status})`);
      return j;
    },
    onSuccess: () => { invalidate(); onClose(); },
  });

  const del = useMutation({
    mutationFn: async () => {
      const res = await apiFetch(`/planograms/${id}`, { method: 'DELETE' });
      const j = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(j?.error ?? `Delete failed (${res.status})`);
      return j;
    },
    onSuccess: () => { invalidate(); onClose(); },
  });

  const busy = save.isPending || activate.isPending || del.isPending;
  const err = save.error || activate.error || del.error;
  const lockField = { opacity: 0.6, pointerEvents: 'none' };

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 60 }}>
      <div style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.35)' }} onClick={onClose} />
      <div style={{ position: 'absolute', top: 0, right: 0, bottom: 0, width: 'min(520px, 96vw)', background: 'var(--bg-card)', borderLeft: '1px solid var(--border)', boxShadow: '-8px 0 24px rgba(0,0,0,0.18)', display: 'flex', flexDirection: 'column' }}>
        <div style={{ padding: '14px 18px', borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'center', gap: 10 }}>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--text-primary)' }}>Edit planogram</div>
            <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>
              {[row.customerName, row.subCategory, row.layoutName, row.status].filter(Boolean).join(' · ')}
            </div>
          </div>
          <Button size="sm" variant="ghost" onClick={onClose}>✕</Button>
        </div>

        <div style={{ flex: 1, overflow: 'auto', padding: 18, display: 'flex', flexDirection: 'column', gap: 14 }}>
          {!isDraft && (
            <div style={{ fontSize: 12, color: 'var(--text-muted)', background: 'var(--border-soft)', border: '1px solid var(--border)', borderRadius: 'var(--radius)', padding: '8px 10px' }}>
              Sub-category and layout are locked once a version is <b>{row.status}</b> (they define the grain). Status and the effective dates are editable — a bad combination is rejected with a message.
            </div>
          )}
          <div>
            <Label>Display name</Label>
            <Input value={form.name} onChange={set('name')} placeholder="e.g. WW NUTS 3-bay Budget Families" />
          </div>
          <div>
            <Label>Status</Label>
            <Select value={form.status} onChange={set('status')}>
              {['draft', 'active', 'superseded'].map((s) => <option key={s} value={s}>{s[0].toUpperCase() + s.slice(1)}</option>)}
            </Select>
          </div>
          <div style={isDraft ? undefined : lockField}>
            <Label>Sub-Category</Label>
            <Select value={form.subCategory} onChange={set('subCategory')} disabled={!isDraft}>
              {[...new Set([form.subCategory, ...subCategories].filter(Boolean))].map((s) => <option key={s} value={s}>{s}</option>)}
            </Select>
          </div>
          <div style={isDraft ? undefined : lockField}>
            <Label>Layout name</Label>
            <Input value={form.layoutName} onChange={set('layoutName')} disabled={!isDraft} />
          </div>
          <div style={{ display: 'flex', gap: 10 }}>
            <div style={{ flex: 1 }}>
              <Label>Effective from</Label>
              <Input type="date" value={form.effectiveFrom} onChange={set('effectiveFrom')} />
            </div>
            <div style={{ flex: 1 }}>
              <Label>Effective to</Label>
              <Input type="date" value={form.effectiveTo} onChange={set('effectiveTo')} />
              <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 3 }}>Blank = current (open).</div>
            </div>
          </div>
          <div>
            <Label>Notes</Label>
            <textarea value={form.notes} onChange={set('notes')} rows={4}
              style={{ width: '100%', boxSizing: 'border-box', resize: 'vertical', padding: '8px 10px', fontSize: 13, borderRadius: 'var(--radius)', border: '1px solid var(--border)', background: 'var(--bg-input, var(--bg-card))', color: 'var(--text-body)', fontFamily: 'inherit' }} />
          </div>
          {isDraft && (
            <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>
              This version is a <b>draft</b> — Activate to make it the current version (supersedes the prior active one), or Delete to discard.
            </div>
          )}
        </div>

        <div style={{ padding: '12px 18px', borderTop: '1px solid var(--border)', display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          {isDraft && (confirmDelete
            ? (
              <Button size="sm" variant="destructive" disabled={busy} onClick={() => del.mutate()}>
                {del.isPending ? 'Deleting…' : 'Confirm delete'}
              </Button>
            )
            : <Button size="sm" variant="destructive" onClick={() => setConfirmDelete(true)}>Delete</Button>)}
          {err && <span style={{ fontSize: 11, color: 'var(--danger)', flex: 1, minWidth: 120 }}>{String(err.message ?? err)}</span>}
          {!err && <span style={{ flex: 1 }} />}
          <Button size="sm" onClick={onClose}>Cancel</Button>
          {isDraft && <Button size="sm" variant="primary" disabled={busy} onClick={() => activate.mutate()}>{activate.isPending ? 'Activating…' : 'Activate'}</Button>}
          <Button size="sm" variant="primary" disabled={busy} onClick={() => save.mutate()}>{save.isPending ? 'Saving…' : 'Save'}</Button>
        </div>
      </div>
    </div>
  );
}

function PlanogramDrawer({ id, onClose, apiFetch, qc }) {
  const [dlError, setDlError] = useState(null);
  const { data, isLoading } = useQuery({
    queryKey: ['planogram', id],
    queryFn: () => apiFetch(`/planograms/${id}`).then((r) => r.json()),
    staleTime: 30_000,
  });
  const header = data?.header;
  const positions = data?.positions ?? [];
  const brands = data?.brands ?? [];
  const storeReach = data?.storeReach;
  const totalFacings = header?.totalFacings ?? brands.reduce((a, b) => a + (b.facings || 0), 0);

  // Scan-velocity overlay (Matthew #4): a shared blob keyed by account+EAN.
  const { data: overlay } = useBlob('planogram-scan-overlay');
  const accountKey = ACCOUNT_BY_CUS[header?.customerCode];
  const perf = useMemo(
    () => new Map(Object.entries(overlay?.byAccount?.[accountKey] ?? {})),
    [overlay, accountKey],
  );
  const acctMeta = accountKey ? overlay?.meta?.accounts?.[accountKey] : null;
  const perfMeta = accountKey && acctMeta
    ? { account: accountKey, accountName: header?.customerName, periodEndDate: acctMeta.periodEndDate, velocityBasis: acctMeta.velocityBasis }
    : null;

  const activate = useMutation({
    mutationFn: () => apiFetch(`/planograms/${id}/activate`, { method: 'PUT' }).then((r) => r.json()),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['planograms'] }); qc.invalidateQueries({ queryKey: ['planogram', id] }); },
  });

  async function download() {
    setDlError(null);
    try {
      const res = await apiFetch(`/planograms/${id}/file`);
      const body = await res.json().catch(() => ({}));
      if (!res.ok || !body.base64) throw new Error(body?.error ?? `Download failed (${res.status})`);
      const bytes = Uint8Array.from(atob(body.base64), (c) => c.charCodeAt(0));
      const url = URL.createObjectURL(new Blob([bytes], { type: 'application/octet-stream' }));
      const a = document.createElement('a');
      a.href = url; a.download = body.fileName || 'planogram.psa'; a.click();
      URL.revokeObjectURL(url);
    } catch (err) { setDlError(String(err.message ?? err)); }
  }

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 60 }}>
      <div style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.4)' }} onClick={onClose} />
      <div style={{ position: 'absolute', top: 0, right: 0, bottom: 0, width: 'min(1100px, 98vw)', background: 'var(--bg-page, var(--bg-card))', borderLeft: '1px solid var(--border)', boxShadow: '-8px 0 24px rgba(0,0,0,0.22)', display: 'flex', flexDirection: 'column' }}>
        <div style={{ padding: '14px 18px', borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'center', gap: 10 }}>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--text-primary)' }}>{header?.name ?? header?.layoutName ?? `Planogram ${id}`}</div>
            <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>
              {header ? [header.customerName, header.subCategory, header.layoutName, `eff. ${fmtDate(header.effectiveFrom)}`, header.status].filter(Boolean).join(' · ') : ''}
            </div>
            {dlError && <div style={{ fontSize: 11, color: 'var(--danger)' }}>{dlError}</div>}
          </div>
          {header?.status === 'draft' && (
            <Button size="sm" variant="primary" disabled={activate.isPending} onClick={() => activate.mutate()}>
              {activate.isPending ? 'Activating…' : 'Activate'}
            </Button>
          )}
          <Button size="sm" onClick={download}>Download .PSA</Button>
          <Button size="sm" variant="ghost" onClick={onClose}>✕</Button>
        </div>

        <div style={{ flex: 1, overflow: 'auto', padding: 18, display: 'flex', flexDirection: 'column', gap: 16 }}>
          {isLoading && <div style={{ fontSize: 13, color: 'var(--text-muted)' }}>Loading planogram…</div>}
          {header && (
            <>
              <ShareOfShelfBar brands={brands} totalFacings={totalFacings} ownBrandSharePct={header.ownBrandSharePct} />
              {storeReach && (
                <div style={{ fontSize: 12, color: 'var(--text-body)', background: 'var(--bg-card)', border: '1px solid var(--border-soft)', borderRadius: 6, padding: '8px 12px' }}>
                  <b>Fleet reach (indicative):</b> ~{storeReach.storesAtSize.toLocaleString()} of {storeReach.totalWithSize.toLocaleString()} {header.customerName} stores
                  {' '}(<b>{storeReach.pct}%</b>) run a {storeReach.baySize}-bay layout for this category.
                  <span style={{ color: 'var(--text-muted)' }}> Bay size from Store Master — a guide (most-common layout), not a per-store list.</span>
                </div>
              )}
              <PlanogramViewer positions={positions} meta={{ bayCount: header.bayCount, shelfCount: header.shelfCount, widthCm: header.widthCm, bayGeometry: parseBayGeometry(header.bayGeometry) }} perf={perf} perfMeta={perfMeta} />
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function ShareOfShelfBar({ brands, totalFacings, ownBrandSharePct }) {
  const top = brands.slice(0, 14);
  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', marginBottom: 8 }}>
        <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--text-primary)' }}>Share of shelf</div>
        <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>
          {totalFacings} facings{ownBrandSharePct != null ? ` · Majans ${Number(ownBrandSharePct).toFixed(1)}%` : ''}
        </div>
      </div>
      <div style={{ display: 'flex', height: 30, borderRadius: 6, overflow: 'hidden', border: '1px solid var(--border-soft)' }}>
        {top.map((b) => {
          const pct = totalFacings ? (b.facings / totalFacings) * 100 : 0;
          const c = b.ownBrand ? brandColor('majans') : brandColor(b.brand);
          return (
            <div key={b.brand} title={`${b.brand}: ${pct.toFixed(1)}% (${b.facings} facings)`}
              style={{ width: `${pct}%`, background: c, display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden' }}>
              {pct > 6 && <span style={{ color: '#fff', fontSize: 11, fontWeight: 600, whiteSpace: 'nowrap', padding: '0 4px' }}>{b.brand}</span>}
            </div>
          );
        })}
      </div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px 14px', marginTop: 8 }}>
        {top.map((b) => {
          const pct = totalFacings ? (b.facings / totalFacings) * 100 : 0;
          const c = b.ownBrand ? brandColor('majans') : brandColor(b.brand);
          return (
            <span key={b.brand} style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 12, color: 'var(--text-body)' }}>
              <span style={{ width: 10, height: 10, borderRadius: 2, background: c }} />
              <span style={{ fontWeight: b.ownBrand ? 700 : 400 }}>{b.brand}</span>
              <span style={{ color: 'var(--text-muted)' }}>{pct.toFixed(1)}%</span>
            </span>
          );
        })}
      </div>
    </div>
  );
}

// ── Upload modal: preview + metadata confirm + commit ──
function UploadModal({ upload, customers, subCategories, setForm, onCommit, onClose }) {
  const { fileName, loading, preview, form, committing, error, result } = upload;
  const missing = preview?.missing ?? [];
  const canCommit = form && form.customerCode && form.subCategory && form.layoutName && form.effectiveFrom && !committing;
  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 70, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.45)' }} onClick={onClose} />
      <div style={{ position: 'relative', width: 'min(620px, 96vw)', maxHeight: '88vh', overflow: 'auto', background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 'var(--radius, 8px)', padding: 20, display: 'flex', flexDirection: 'column', gap: 14 }}>
        <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--text-primary)' }}>Import planogram — {fileName}</div>
        {loading && <div style={{ fontSize: 13, color: 'var(--text-muted)' }}>Parsing .PSA…</div>}
        {error && <div style={{ color: 'var(--danger)', fontSize: 13 }}>{error}</div>}

        {result && (
          <>
            <div style={{ fontSize: 13, color: 'var(--text-body)' }}>
              Imported as {result.status} — {result.positionCount} products across {result.bayCount} bays.
              {result.priorVersion ? ' Prior version superseded.' : ''}
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end' }}><Button size="sm" variant="primary" onClick={onClose}>Close</Button></div>
          </>
        )}

        {!result && preview && form && (
          <>
            <div style={{ fontSize: 13, color: 'var(--text-body)' }}>
              <b>{preview.positionCount}</b> products · <b>{preview.bayCount}</b> bays · <b>{preview.shelfCount}</b> shelves ·
              {' '}<b>{preview.matchedEans}</b> EANs in Scan master, <b>{preview.unmatchedEans}</b> unmatched
            </div>
            {preview.priorVersion && (
              <div style={{ fontSize: 12, color: 'var(--warn-text)', background: 'var(--warn-soft)', border: '1px solid var(--warn-border)', borderRadius: 6, padding: '8px 10px' }}>
                A current version exists (eff. {fmtDate(preview.priorVersion.effectiveFrom)}). Importing will supersede it.
              </div>
            )}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
              <div>
                <Label>Customer</Label>
                <Select value={form.customerCode} onChange={(e) => setForm('customerCode')(e.target.value)}>
                  <option value="">Select…</option>
                  {customers.map((c) => <option key={c.customerCode} value={c.customerCode}>{c.name}</option>)}
                </Select>
              </div>
              <div>
                <Label>Sub-Category</Label>
                <Select value={form.subCategory} onChange={(e) => setForm('subCategory')(e.target.value)}>
                  <option value="">Select…</option>
                  {[...new Set([form.subCategory, ...subCategories].filter(Boolean))].map((s) => <option key={s} value={s}>{s}</option>)}
                </Select>
              </div>
              <div style={{ gridColumn: '1 / -1' }}>
                <Label>Layout name</Label>
                <Input value={form.layoutName} onChange={(e) => setForm('layoutName')(e.target.value)} />
              </div>
              <div>
                <Label>Effective from</Label>
                <Input type="date" value={form.effectiveFrom} onChange={(e) => setForm('effectiveFrom')(e.target.value)} />
              </div>
              <div>
                <Label>Display name</Label>
                <Input value={form.name} onChange={(e) => setForm('name')(e.target.value)} />
              </div>
            </div>
            {preview.suggested?.retailer && (
              <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>
                Auto-detected from file: {preview.suggested.retailer}
                {preview.suggested.sourceCategory ? ` · ${preview.suggested.sourceCategory}` : ''}
                {preview.suggested.cluster ? ` · ${preview.suggested.cluster}` : ''}
              </div>
            )}
            {preview.warnings?.length > 0 && (
              <details style={{ fontSize: 12, color: 'var(--text-muted)' }}>
                <summary>{preview.warnings.length} parser warning(s)</summary>
                <ul style={{ margin: '6px 0 0', paddingLeft: 18 }}>{preview.warnings.slice(0, 12).map((w, i) => <li key={i}>{w}</li>)}</ul>
              </details>
            )}
            {preview.sampleUnmatched?.length > 0 && (
              <details style={{ fontSize: 12, color: 'var(--text-muted)' }}>
                <summary>{preview.unmatchedEans} EAN(s) not in Scan master (kept with PSA name)</summary>
                <ul style={{ margin: '6px 0 0', paddingLeft: 18, maxHeight: 140, overflow: 'auto' }}>
                  {preview.sampleUnmatched.map((u, i) => <li key={i}>{u.ean} — {u.productName} {u.brand ? `(${u.brand})` : ''}</li>)}
                </ul>
              </details>
            )}
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', alignItems: 'center' }}>
              {missing.length > 0 && <span style={{ fontSize: 12, color: 'var(--warn-text)', flex: 1 }}>Fill: {missing.join(', ')}</span>}
              <Button size="sm" onClick={onClose}>Cancel</Button>
              <Button size="sm" disabled={!canCommit} onClick={() => onCommit(false)} title="Import without activating — review, then Activate from the edit panel">
                {committing ? '…' : 'Save as draft'}
              </Button>
              <Button size="sm" variant="primary" disabled={!canCommit} onClick={() => onCommit(true)}>
                {committing ? 'Importing…' : 'Import as active version'}
              </Button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
